import React from 'react';

import {
	FormattedDate,
	FormattedMessage,
	FormattedRelativeTime,
	type IntlShape,
	type MessageDescriptor,
} from 'react-intl';

import { selectUnit } from '@atlassian/recent-work-ui';

import { isMoreThanOneWeekAgo } from '../../../../../common/ui/event-type-message';
import { type ActivityEnrichment } from '../../../../../common/ui/quick-find/types';

import {
	clickedEventTypeMessages,
	commentedEventTypeMessages,
	createdEventTypeMessages,
	editedEventTypeMessages,
	type EventTypeMessages,
	interactedEventTypeMessages,
	mentionedEventTypeMessages,
	messages,
	reactedEventTypeMessages,
	readEventTypeMessages,
	sharedEventTypeMessages,
	viewedEventTypeMessages,
} from './messages';
import { parseRecommendedReason, RecommendedEventType, RecommendedType } from './types';
import { resolveDisplayTimestamp } from './utils';

const LOCAL_EVENT_TYPE_MESSAGES: Record<RecommendedEventType, EventTypeMessages> = {
	clicked: clickedEventTypeMessages,
	commented: commentedEventTypeMessages,
	created: createdEventTypeMessages,
	edited: editedEventTypeMessages,
	interacted: interactedEventTypeMessages,
	mentioned: mentionedEventTypeMessages,
	reacted: reactedEventTypeMessages,
	read: readEventTypeMessages,
	shared: sharedEventTypeMessages,
	viewed: viewedEventTypeMessages,
};

/**
 * Returns the appropriate message descriptor for a given event type.
 * @param eventType - The type of the event.
 * @param options - Options to configure the message.
 * @returns The message descriptor for the event type.
 */
const messageForEventType = (
	eventType: RecommendedEventType,
	options: { isAbsolute?: boolean } = {},
): MessageDescriptor => {
	const { isAbsolute = true } = options;
	const eventTypeMessage = LOCAL_EVENT_TYPE_MESSAGES.hasOwnProperty(eventType)
		? LOCAL_EVENT_TYPE_MESSAGES[eventType]
		: viewedEventTypeMessages; // default to viewed messages

	return isAbsolute ? eventTypeMessage.absolute : eventTypeMessage.relative;
};

// Sort enum values by length descending to avoid substring false positives,
const sortedRecommendedEventTypes = Object.values(RecommendedEventType).sort(
	(a, b) => b.length - a.length,
);

/**
 * Returns the `feature` value from `reason` if it matches one of the keys in
 * `LOCAL_EVENT_TYPE_MESSAGES` (case-insensitive), otherwise returns `null`.
 */
export const getEventTypeFromReasonFeature = (
	feature: string | undefined,
): RecommendedEventType | null => {
	if (!feature) {
		return null;
	}
	const lowerFeature = feature.toLowerCase();
	if (LOCAL_EVENT_TYPE_MESSAGES.hasOwnProperty(lowerFeature)) {
		return lowerFeature as RecommendedEventType;
	}
	return null;
};

/**
 * Extracts the event type from `reason.text` by checking if the text contains
 * a known event type keyword. Returns the matching `RecommendedEventType` value,
 * or `RecommendedEventType.VIEWED` as a default fallback.
 */
export const extractEventTypeFromReasonText = (text: string): RecommendedEventType => {
	const lowerText = text.toLowerCase();

	for (const eventType of sortedRecommendedEventTypes) {
		if (lowerText.includes(eventType)) {
			return eventType;
		}
	}

	return RecommendedEventType.VIEWED;
};

type RecommendedDocumentEventTypeMessageProps = {
	reason: string;
	activityEnrichment?: ActivityEnrichment;
	intl: IntlShape;
	logException: (error: Error) => Promise<void>;
};

export const RecommendedDocumentEventTypeMessage = ({
	reason,
	activityEnrichment,
	intl,
	logException,
}: RecommendedDocumentEventTypeMessageProps): React.ReactNode => {
	const parsedReason = parseRecommendedReason(reason, logException);

	if (!parsedReason) {
		return null;
	}

	const eventType =
		getEventTypeFromReasonFeature(parsedReason.feature) ??
		extractEventTypeFromReasonText(parsedReason.text);

	const { type } = parsedReason;

	const timestamp = resolveDisplayTimestamp(
		type,
		eventType,
		parsedReason.timestamp,
		activityEnrichment,
	);

	const collaborator =
		type === RecommendedType.NO_COLLABORATOR
			? intl.formatMessage(messages.you)
			: intl.formatMessage(messages.collaborator);

	const isAbsolute = isMoreThanOneWeekAgo(timestamp);

	const message = messageForEventType(eventType, {
		isAbsolute,
	});

	const { value, unit } = selectUnit(new Date(timestamp).getTime(), new Date(), {
		day: 7,
	});

	const values = isAbsolute
		? {
				// Show the absolute time if the activity was more than a week ago
				// e.g: You viewed on January 1, 2021
				absoluteTime: (
					<FormattedDate value={timestamp} month="short" day="2-digit" year="numeric" />
				),
			}
		: {
				// Show the relative time if the activity was within a week ago
				// e.g: You viewed 2 hours ago
				relativeTime: <FormattedRelativeTime value={value} unit={unit} />,
			};

	return <FormattedMessage {...message} values={{ ...values, collaborator }} />;
};
